import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import { Phone, Lock, CheckCircle, Globe, ShieldCheck, Briefcase, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCompanySettings } from "../context/CompanySettingsContext";
import { authAPI } from "../api/apiService";
import { tokenManager } from "../utils/tokenManager";

const ROLE_DETAILS = {
    SUPERADMIN: {
        label: "SUPER ADMIN",
        desc: "Restricted system administrator access",
        iconName: "Lock",
        color: "#3B82F6",
        bgLight: "#EFF6FF",
        iconBg: "bg-blue-50 text-blue-600 border border-blue-100/50",
    }
};

export default function SuperAdminLoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { companySettings } = useCompanySettings();
    const [step, setStep] = useState(1); // 1: Role, 2: Phone, 3: OTP (Reset), 4: Password (Login)
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("IN");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState("SUPERADMIN");
    const [isResetMode, setIsResetMode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [identifiedUserName, setIdentifiedUserName] = useState("");

    const roles = [
        {
            id: "SUPERADMIN",
            name: "SUPER ADMIN",
            color: "from-purple-600 to-indigo-600",
            icon: <Lock className="w-8 h-8" />,
        }
    ];

    const currentCountry = { code: "IN", name: "India", prefix: "+91", dialLength: 10 };

    const handleRoleSubmit = () => {
        if (!selectedRole) {
            setError("Please confirm the role to continue");
            return;
        }
        setError("");
        setStep(2);
    };

    const handlePhoneSubmit = async () => {
        setError("");
        if (!phone || phone.length !== currentCountry.dialLength) {
            setError(`Please enter a valid ${currentCountry.dialLength}-digit ${currentCountry.name} phone number`);
            return;
        }

        if (isResetMode) {
            setLoading(true);
            try {
                const response = await authAPI.sendOTP(phone);
                if (response.data && response.data.otp) {
                    console.log("========================================");
                    console.log(`DEBUG: OTP for ${phone} is: ${response.data.otp}`);
                    console.log("========================================");
                }
                setSuccessMessage("OTP sent successfully to your phone.");
                setStep(3); // Go to OTP step
            } catch (err) {
                setError(err.response?.data?.detail || "Failed to send OTP.");
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(true);
            try {
                const lookupResponse = await authAPI.lookupUser({ phone });
                if (lookupResponse.data && lookupResponse.data.found) {
                    let name = lookupResponse.data.name;
                    if (name === "Super Admin" || !name) {
                        name = "Greenheap";
                    }
                    setIdentifiedUserName(name);
                    setStep(4);
                } else {
                    setError("User not found.");
                }
            } catch (err) {
                console.warn("User lookup failed:", err);
                setError(err.response?.data?.detail || "User not found.");
                setIdentifiedUserName("");
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePasswordSubmit = async () => {
        setError("");
        if (!password) {
            setError("Please enter a password");
            return;
        }

        setLoading(true);
        try {
            const response = await authAPI.login({ phone, password, role: "SUPERADMIN" });

            if (response.data.token) {
                tokenManager.setToken(response.data.token);
            }

            const backendUser = response.data.user;

            if (!backendUser.is_super_admin && !backendUser.roles?.some(r => r.name === "SUPERADMIN")) {
                throw new Error("Access Denied: You do not have Super Admin privileges.");
            }

            const userData = {
                ...backendUser,
                role: "SUPERADMIN",
                is_super_admin: true,
                name: backendUser?.first_name || backendUser?.name || 'Super Admin'
            };

            login(userData);
            navigate("/super-admin");

        } catch (err) {
            console.error("Login Error:", err);
            const errorMsg = err.response?.data?.detail ||
                err.message ||
                "Login failed. Please check your credentials.";
            setError(errorMsg);
            setPassword("");
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async () => {
        setError("");
        if (!otp || otp.length !== 6) {
            setError("Please enter a valid 6-digit OTP.");
            return;
        }
        if (!newPassword || newPassword.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            await authAPI.resetPassword({
                phone,
                otp,
                new_password: newPassword,
                confirm_password: confirmPassword
            });
            setSuccessMessage("Password reset successfully! Please login with your new password.");
            setIsResetMode(false);
            setPassword("");
            
            try {
                const lookupResponse = await authAPI.lookupUser({ phone });
                if (lookupResponse.data && lookupResponse.data.found) {
                    let name = lookupResponse.data.name;
                    if (name === "Super Admin" || !name) {
                        name = "Greenheap";
                    }
                    setIdentifiedUserName(name);
                }
            } catch (lookupErr) {
                console.warn("User lookup failed after reset:", lookupErr);
            }
            
            setStep(4);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to reset password.");
        } finally {
            setLoading(false);
        }
    };

    const startForgotFlow = () => {
        setIsResetMode(true);
        setStep(2);
        setError("");
        setSuccessMessage("");
    };

    const cancelForgotFlow = () => {
        setIsResetMode(false);
        if (phone && identifiedUserName) {
            setStep(4);
        } else {
            setStep(2);
        }
        setError("");
        setSuccessMessage("");
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-inter bg-gray-50">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex flex-col justify-center items-center bg-gradient-to-br from-[#022c22] to-[#0b4534] relative overflow-hidden text-white p-12">
                {/* Top-left dot grid pattern */}
                <div className="absolute top-10 left-10 opacity-20 pointer-events-none">
                    <div className="grid grid-cols-5 gap-2">
                        {Array.from({ length: 25 }).map((_, i) => (
                            <div key={i} className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        ))}
                    </div>
                </div>

                {/* Abstract shapes & glowing lines/gradients */}
                <div className="absolute top-0 left-0 w-full h-full opacity-35 pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-emerald-500 to-[#10B981] rounded-full filter blur-3xl opacity-20 mix-blend-screen"></div>
                    <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-gradient-to-tr from-[#10B981] to-[#047857] rounded-full filter blur-3xl opacity-30 mix-blend-screen"></div>
                </div>

                {/* Left Side Decorative Background Curves (SVG) */}
                <div className="absolute bottom-0 left-0 w-full pointer-events-none">
                    <svg viewBox="0 0 500 500" className="w-full h-auto translate-y-12 scale-110 opacity-70" xmlns="http://www.w3.org/2000/svg">
                        <path fill="url(#grad1)" fillOpacity="0.4" d="M0,320 C150,420 350,220 500,400 L500,500 L0,500 Z"></path>
                        <path fill="url(#grad2)" fillOpacity="0.3" d="M0,280 C200,480 300,320 500,440 L500,500 L0,500 Z"></path>
                        <path fill="url(#grad3)" fillOpacity="0.35" d="M0,380 C100,460 250,300 500,480 L500,500 L0,500 Z"></path>
                        <defs>
                            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#10B981" />
                                <stop offset="100%" stopColor="#047857" />
                            </linearGradient>
                            <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#059669" />
                                <stop offset="100%" stopColor="#064e3b" />
                            </linearGradient>
                            <linearGradient id="grad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#10B981" />
                                <stop offset="100%" stopColor="#0f766e" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                <div className="z-10 text-center flex flex-col items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mb-10"
                    >
                        <div className="w-36 h-36 mx-auto mb-4 flex items-center justify-center">
                            <img
                                src="/brand-logo.png"
                                alt="Logo"
                                className="w-full h-full object-contain filter drop-shadow-xl"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/default-logo.png";
                                }}
                            />
                        </div>
                        <h1 className="text-3xl font-extrabold mb-1 tracking-widest font-outfit text-white">GIO BILLING</h1>
                        <p className="text-blue-200/60 text-xs tracking-[0.2em] font-semibold uppercase mb-8">Bill Smarter, Grow Faster</p>
                        <h2 className="text-3xl font-bold tracking-tight text-white mb-2 leading-snug">
                            Smart Billing Solutions <br /> for Modern Business
                        </h2>
                        <div className="w-16 h-[3px] bg-emerald-500 mx-auto rounded-full mt-4 mb-10"></div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                        className="space-y-6 w-full max-w-xs text-left"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center bg-white/5 backdrop-blur-md shadow-inner text-white">
                                <Globe className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white tracking-wider text-xs uppercase leading-none">Global</h4>
                                <p className="text-blue-200/70 text-xs mt-1">Access anywhere, anytime</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center bg-white/5 backdrop-blur-md shadow-inner text-white">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white tracking-wider text-xs uppercase leading-none">Secure</h4>
                                <p className="text-blue-200/70 text-xs mt-1">Your data is always protected</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center bg-white/5 backdrop-blur-md shadow-inner text-white">
                                <Lucide.Zap className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white tracking-wider text-xs uppercase leading-none">Reliable</h4>
                                <p className="text-blue-200/70 text-xs mt-1">Built for performance & trust</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div className="flex flex-col items-center justify-center bg-gray-50 p-6 lg:p-12 relative min-h-screen">
                
                {/* Language selector in top right corner */}
                <div className="absolute top-6 right-6 z-20">
                    <button className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm transition-all cursor-pointer">
                        <Globe className="w-3.5 h-3.5 text-gray-400" />
                        <span>English</span>
                        <Lucide.ChevronDown className="w-3 h-3 text-gray-400" />
                    </button>
                </div>

                {/* Card Container */}
                <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 p-8 pt-10 relative mt-8">
                    
                    {/* Centered user silhouette overlapping top of card */}
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2">
                        <div className="w-20 h-20 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-lg">
                            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                                <Lucide.User className="w-8 h-8" />
                            </div>
                        </div>
                    </div>

                    <div className="lg:hidden text-center mb-8 mt-6">
                        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <img
                                src="/brand-logo.png"
                                alt="Logo"
                                className="w-full h-full object-contain filter drop-shadow-md"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/default-logo.png";
                                }}
                            />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 font-outfit tracking-wide">GIO BILLING</h1>
                        <p className="text-emerald-600 text-xs font-semibold tracking-wider mt-1 uppercase">Super Admin Portal</p>
                    </div>

                    {/* Heading Section */}
                    <div className="mb-8 text-center mt-4">
                        <h2 className="text-2xl font-extrabold text-gray-900 mb-2 tracking-tight">
                            {isResetMode ? "Reset Password" : "System Login"}
                        </h2>
                        <p className="text-gray-500 text-sm">
                            {isResetMode ? "Verify your identity to reset password." : "Restricted access. Authorized personnel only."}
                        </p>
                    </div>

                    {/* Step 1: Role Selection */}
                    {step === 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                        >
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Select Role</p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setSelectedRole("SUPERADMIN")}
                                    className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between group cursor-pointer ${
                                        selectedRole === "SUPERADMIN"
                                            ? "border-emerald-500 bg-emerald-50/10 shadow-md shadow-emerald-500/5 ring-1 ring-emerald-500"
                                            : "border-gray-200 hover:border-emerald-300 hover:bg-gray-50"
                                    }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600 border border-emerald-100/50 transition-all">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold text-sm leading-tight transition-colors ${selectedRole === "SUPERADMIN" ? "text-emerald-950" : "text-gray-800"}`}>
                                                Super Admin
                                            </h3>
                                            <p className="text-gray-500 text-xs mt-1 leading-normal">
                                                Complete system administration rights.
                                            </p>
                                        </div>
                                    </div>
                                    <Lucide.ChevronRight className={`w-5 h-5 transition-transform ${
                                        selectedRole === "SUPERADMIN" ? "text-emerald-600 translate-x-1" : "text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5"
                                    }`} />
                                </button>
                            </div>

                            {error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2 text-red-600 text-sm">
                                    <span>⚠️</span>
                                    <span>{typeof error === 'string' ? error : 'An error occurred'}</span>
                                </div>
                            )}

                            <button
                                onClick={handleRoleSubmit}
                                className="w-full mt-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/10 hover:shadow-emerald-600/20 cursor-pointer"
                            >
                                <span>Continue</span>
                                <Lucide.ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}

                    {/* Step 2: Phone Input */}
                    {step === 2 && (
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">{isResetMode ? "Identify Account" : "Login"}</h3>
                                    <p className="text-xs text-emerald-600 font-medium">Role: SUPER ADMIN</p>
                                </div>
                                <button 
                                    onClick={() => {
                                        setIsResetMode(false);
                                        setStep(1);
                                        setPhone("");
                                        setError("");
                                    }} 
                                    className="text-xs text-gray-400 hover:text-gray-600 underline font-medium cursor-pointer"
                                >
                                    Change Role
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Phone Number
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 min-w-[3rem]">
                                            <span className="text-gray-500 font-semibold text-sm">{currentCountry.prefix}</span>
                                            <div className="h-4 w-px bg-gray-300"></div>
                                        </div>
                                        <input
                                            type="tel"
                                            maxLength={10}
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                                            placeholder="Enter 10-digit number"
                                            className="w-full pl-16 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400"
                                        />
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                                    {typeof error === 'string' ? error : 'An error occurred'}
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        if (isResetMode) {
                                            cancelForgotFlow();
                                        } else {
                                            setStep(1);
                                            setPhone("");
                                            setError("");
                                        }
                                    }}
                                    className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 text-sm transition-colors cursor-pointer"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handlePhoneSubmit}
                                    disabled={loading}
                                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-md shadow-emerald-600/20 text-sm disabled:opacity-50 transition-colors cursor-pointer"
                                >
                                    {loading ? "Processing..." : (isResetMode ? "Send OTP" : "Continue")}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 3: OTP & New Password (Reset Mode Only) */}
                    {step === 3 && isResetMode && (
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="mb-6 pb-4 border-b border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-900">Reset Password</h3>
                                <p className="text-xs text-gray-500">OTP Sent to {currentCountry.prefix} {phone}</p>
                            </div>

                            {successMessage && (
                                <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg flex items-center gap-2 border border-green-100">
                                    <CheckCircle className="w-4 h-4" /> {successMessage}
                                </div>
                            )}

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Enter OTP</label>
                                    <input
                                        type="text"
                                        maxLength={6}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        placeholder="6-digit OTP"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">New Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            placeholder="Min 6 chars"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 outline-none cursor-pointer"
                                        >
                                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Confirm Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            placeholder="Confirm new password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 outline-none cursor-pointer"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {error && <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">{error}</div>}

                            <button
                                onClick={handleResetSubmit}
                                disabled={loading}
                                className="w-full mt-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-md shadow-emerald-600/20 transition-colors text-sm cursor-pointer"
                            >
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                            <button
                                onClick={() => { setStep(2); setError(""); setOtp(""); setSuccessMessage(""); }}
                                className="w-full mt-3 py-2.5 text-sm text-gray-500 hover:text-gray-700 font-medium cursor-pointer"
                            >
                                Back
                            </button>
                        </motion.div>
                    )}

                    {/* Step 4: Password (Login Mode Only) */}
                    {step === 4 && (
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 font-outfit">
                                            Enter Password
                                        </h3>
                                        <p className="text-xs text-gray-500">Phone: {currentCountry.prefix} {phone}</p>
                                    </div>
                                    <button onClick={() => { setStep(2); setPassword(""); setError(""); setIdentifiedUserName(""); }} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
                                        Edit Number
                                    </button>
                                </div>

                                {identifiedUserName && (
                                    <div className="text-left mt-2 p-2 bg-emerald-50/50 rounded-lg border border-emerald-100/50">
                                        <h4 className="text-sm font-bold text-gray-800">Welcome, {identifiedUserName}!</h4>
                                    </div>
                                )}
                            </div>

                            {successMessage && (
                                <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg flex items-center gap-2 border border-green-100">
                                    <CheckCircle className="w-4 h-4" /> {successMessage}
                                </div>
                            )}

                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-1.5">
                                        <label className="block text-xs font-medium text-gray-700">
                                            Password
                                        </label>
                                        <button onClick={startForgotFlow} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
                                            Forgot Password?
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter system password"
                                            onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                                            className="w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 outline-none cursor-pointer"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                                    {typeof error === 'string' ? error : 'An error occurred'}
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => { setStep(2); setPassword(""); setError(""); }}
                                    className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 text-sm transition-colors cursor-pointer"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handlePasswordSubmit}
                                    disabled={loading}
                                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-md shadow-emerald-600/20 text-sm disabled:opacity-50 transition-colors cursor-pointer"
                                >
                                    {loading ? "Logging in..." : "Login"}
                                </button>
                            </div>
                        </motion.div>
                    )}

                </div>

                {/* Footer */}
                <div className="w-full max-w-lg mt-8 flex items-center justify-between text-xs text-gray-400 px-2">
                    <div className="flex items-center gap-1.5">
                        <Lucide.ShieldCheck className="w-4 h-4 text-gray-400" />
                        <span>Your security is our priority</span>
                    </div>
                    <div>
                        &copy; 2026 gio billing. All rights reserved.
                    </div>
                </div>

            </div>
        </div>
    );
}
